const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/+$/, "");

if (!API_BASE_URL) {
  throw new Error("REACT_APP_API_BASE_URL is not configured.");
}

export function getApiUrl(path) {
  return `${API_BASE_URL}${path}`;
}

// ---------------------------------------------------------------------------
// Token provider — registered at app startup via setTokenProvider(getToken).
// ---------------------------------------------------------------------------
const _tokenProvider = { getToken: null };

export function setTokenProvider(getToken) {
  _tokenProvider.getToken = getToken;
}

// Get a Clerk JWT. No template name — calling with no args returns the default
// session token, which always exists when the user is signed in.
async function getBearerToken() {
  const fn = _tokenProvider.getToken;
  if (!fn) return null;
  try {
    return await fn() ?? null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Error helpers
// ---------------------------------------------------------------------------
function createApiError(status, message, details) {
  const error = new Error(message || "An unexpected error occurred.");
  error.status = status;
  error.details = details;
  return error;
}

function flattenValidationErrors(errors) {
  if (!errors || typeof errors !== "object") return [];
  return Object.values(errors)
    .flatMap((v) => (Array.isArray(v) ? v : typeof v === "string" ? [v] : []))
    .filter(Boolean);
}

function normalizeErrorMessage(data, response) {
  if (!data) return `Request failed with status ${response.status}`;
  if (typeof data === "string" && data.trim()) return data;
  if (typeof data === "object") {
    if (typeof data.message === "string" && data.message.trim()) return data.message;
    if (typeof data.error   === "string" && data.error.trim())   return data.error;
    if (typeof data.detail  === "string" && data.detail.trim())  return data.detail;
    if (typeof data.title   === "string" && data.title.trim())   return data.title;
    const msgs = flattenValidationErrors(data.errors);
    if (msgs.length > 0) return msgs.join(", ");
  }
  return `Request failed with status ${response.status}`;
}

async function parseResponseBody(response) {
  const contentType = response.headers.get("content-type") || "";
  if (contentType.toLowerCase().includes("application/json")) {
    try { return await response.json(); } catch { return null; }
  }
  try {
    const text = await response.text();
    return text || null;
  } catch {
    return null;
  }
}

function buildSignal(externalSignal, timeoutMs) {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => {
    controller.abort(new DOMException("Request timed out.", "AbortError"));
  }, timeoutMs);

  if (!externalSignal) {
    return { signal: controller.signal, cleanup: () => window.clearTimeout(timeoutId) };
  }

  if (externalSignal.aborted) {
    controller.abort(externalSignal.reason);
  } else {
    externalSignal.addEventListener("abort", () => controller.abort(externalSignal.reason), { once: true });
  }

  return { signal: controller.signal, cleanup: () => window.clearTimeout(timeoutId) };
}

// ---------------------------------------------------------------------------
// Core request
// ---------------------------------------------------------------------------
export async function apiRequest(path, options = {}) {
  const {
    method = "GET",
    body,
    headers = {},
    signal: externalSignal,
    timeoutMs = 30000,
  } = options;

  const { signal, cleanup } = buildSignal(externalSignal, timeoutMs);

  // Build fetch options fresh each call so retries get their own body string
  const makeFetchOptions = (token) => {
    const h = { ...headers };
    if (token) h["Authorization"] = `Bearer ${token}`;
    const opts = { method, headers: h, signal };
    if (body !== undefined) {
      h["Content-Type"] = h["Content-Type"] || "application/json";
      opts.body = h["Content-Type"] === "application/json" ? JSON.stringify(body) : body;
    }
    return opts;
  };

  try {
    const token = await getBearerToken();
    const response = await fetch(getApiUrl(path), makeFetchOptions(token));
    const data = await parseResponseBody(response);

    if (!response.ok) {
      // On 401, get a guaranteed-fresh token and retry once
      if (response.status === 401) {
        const freshToken = await _tokenProvider.getToken?.({ skipCache: true });
        if (freshToken) {
          const retryResponse = await fetch(getApiUrl(path), makeFetchOptions(freshToken));
          const retryData = await parseResponseBody(retryResponse);
          if (!retryResponse.ok) {
            throw createApiError(retryResponse.status, normalizeErrorMessage(retryData, retryResponse), retryData);
          }
          return retryData;
        }
      }

      throw createApiError(response.status, normalizeErrorMessage(data, response), data);
    }

    return data;
  } finally {
    cleanup();
  }
}

export function apiGet(path, options = {}) {
  return apiRequest(path, { ...options, method: "GET" });
}

export function apiPost(path, body, options = {}) {
  return apiRequest(path, { ...options, method: "POST", body });
}

export function apiDelete(path, options = {}) {
  return apiRequest(path, { ...options, method: "DELETE" });
}

export function apiPut(path, body, options = {}) {
  return apiRequest(path, { ...options, method: "PUT", body });
}

export function getErrorMessage(error) {
  if (!error) return "Something went wrong.";
  if (typeof error === "string") return error;
  if (error.name === "AbortError") return "Request was cancelled.";
  if (typeof error.message === "string" && error.message.trim()) return error.message;
  return "Something went wrong.";
}
